import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { BiTime } from "react-icons/bi";
import { validate as isValidUUID } from "uuid";

import AccessDenied from "../common/AccessDenied";
import { useLoading } from "../common/LoadingContext";
import { useToast } from "../common/ToastContext";
import SearchBox from "../common/SearchBox";
import AssignmentModal from "./AssignmentModal";
import AssignmentMainContent from "./AssignmentMainContent";

import { getUserName } from "../../services/auth";
import { getAssignmentsList } from "../../services/assignmentService";
import { currentUserHasRole } from "../../services/userService";

const QUERY_SIZE = 2000;

const AssignmentsDashboard = () => {
  const location = useLocation();
  const [accessDenied, setAccessDenied] = useState(null);
  const [userName, setUserName] = useState("");
  const [filteredAssignments, setFilteredAssignments] = useState([]);
  const [activeFilter, setActiveFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [isRegexMode, setIsRegexMode] = useState(false);
  const [maxPage, setMaxPage] = useState(0);
  const assignmentsPageCache = useRef(null);
  const pageBeforeIdSearch = useRef(null);
  const { setIsLoading } = useLoading();
  const { showToast } = useToast();
  const navigate = useNavigate();

  // An exact assignment ID is searched server-side instead of only filtering the current page.
  const searchedAssignmentId = useMemo(() => {
    const trimmed = searchTerm?.trim();
    return trimmed && isValidUUID(trimmed) ? trimmed : null;
  }, [searchTerm]);

  useEffect(() => {
    if (searchedAssignmentId) {
      if (pageBeforeIdSearch.current === null) {
        pageBeforeIdSearch.current = page;
      }
      if (page !== 1) setPage(1);
    } else if (pageBeforeIdSearch.current !== null) {
      setPage(pageBeforeIdSearch.current);
      pageBeforeIdSearch.current = null;
    }
  }, [searchedAssignmentId]);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const hasReadAccess = await currentUserHasRole("viewer");
        setAccessDenied(!hasReadAccess);
      } catch (error) {
        console.error("Error checking user role:", error);
        setAccessDenied(true);
      }
    };

    const fetchUserName = async () => {
      try {
        const userName = await getUserName();
        if (userName) {
          setUserName(userName);
        }
      } catch (err) {
        console.error("Failed to fetch user name", err);
      }
    };

    checkAccess();
    fetchUserName();
  }, [showToast]);

  useEffect(() => {
    const fetchPageLocation = async () => {
      try {
        const isOpen = new URLSearchParams(location.search).get("open");
        const user = new URLSearchParams(location.search).get("user");
        let searchStr = "";

        if (isOpen) {
          searchStr = "{open}";
        } else if (!isOpen && isOpen !== null) {
          searchStr = "{closed}";
        }

        if (user === "me") {
          searchStr += ` ${userName}`;
        }
        setSearchTerm(searchStr);
        setIsRegexMode(true);
      } catch (err) {
        console.error("Failed to fetch user name", err);
      }
    };

    fetchPageLocation();
  }, [userName]);

  useEffect(() => {
    const fetchAssignments = async () => {
      const pageCacheKey = `${page}:${sortDirection}:${activeFilter}`;
      if (
        !searchedAssignmentId &&
        assignmentsPageCache.current?.key === pageCacheKey
      ) {
        const cachedPage = assignmentsPageCache.current.page;
        setFilteredAssignments(cachedPage.assignments);
        setMaxPage(cachedPage.maxPage);
        return;
      }

      let search_open = null;
      if (activeFilter === "open") {
        search_open = true;
      } else if (activeFilter === "closed") {
        search_open = false;
      }
      try {
        const response = await getAssignmentsList(
          searchedAssignmentId ? 1 : page,
          QUERY_SIZE,
          `created_at:${sortDirection}`,
          search_open,
          searchedAssignmentId ? [searchedAssignmentId] : null,
        );
        if (response.status === 200) {
          const fetchedAssignments = response.data.data.assignments;
          const fetchedMaxPage = Math.ceil(
            response.data.data.total / QUERY_SIZE,
          );
          setFilteredAssignments(fetchedAssignments);
          if (searchedAssignmentId) {
            setMaxPage(1);
          } else {
            setMaxPage(fetchedMaxPage);
            assignmentsPageCache.current = {
              key: pageCacheKey,
              page: {
                assignments: fetchedAssignments,
                maxPage: fetchedMaxPage,
              },
            };
          }
        }
        if (response.status === 404) {
          showToast("No additional assignments found", "warning");
          setPage(page - 1);
        }
      } catch (err) {
        showToast("Failed to load assignments. Please try again.", "error");
      }
    };
    fetchAssignments();
  }, [
    page,
    showToast,
    sortDirection,
    activeFilter,
    searchedAssignmentId,
  ]);

  const handleRefresh = async () => {
    try {
      setIsLoading(true);
      const response = await getAssignmentsList(
        searchedAssignmentId ? 1 : page,
        QUERY_SIZE,
        `created_at:${sortDirection}`,
        activeFilter === "open" ? true : activeFilter === "closed" ? false : null,
        searchedAssignmentId ? [searchedAssignmentId] : null,
      );
      const fetchedAssignments = response.data.data.assignments;
      setFilteredAssignments(fetchedAssignments);
      if (!searchedAssignmentId) {
        const fetchedMaxPage = Math.ceil(
          response.data.data.total / QUERY_SIZE,
        );
        setMaxPage(fetchedMaxPage);
        assignmentsPageCache.current = {
          key: `${page}:${sortDirection}:${activeFilter}`,
          page: { assignments: fetchedAssignments, maxPage: fetchedMaxPage },
        };
      }
      if (selectedAssignment) {
        const updatedAssignment = fetchedAssignments.find(
          (a) => a.id === selectedAssignment.id,
        );
        if (updatedAssignment) {
          setSelectedAssignment(updatedAssignment);
        }
      }
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      showToast("Failed to refresh assignments. Please try again.", "error");
    }
  };

  const handleSearch = useCallback((term, regexMode) => {
    setSearchTerm(term);
    setIsRegexMode(regexMode);
  }, []);

  const handleAssignmentModalClose = () => {
    setIsAssignmentModalOpen(false);
  };

  const handleAssignmentSelect = (assignment) => {
    setSelectedAssignment(assignment);
    setIsAssignmentModalOpen(true);
    navigate(`#${assignment.id}`);
  };

  const getSortDirectionLabel = (field) => {
    if (sortField !== field) return "";
    return sortDirection === "asc" ? "↑" : "↓";
  };

  const clearActiveFilter = () => {
    setActiveFilter("");
  };

  const filterOptions = useMemo(() => {
    const options = [
      {
        label: "All Assignments",
        value: "",
        isRegexMode: false,
        onSelect: () => setActiveFilter(""),
      },
      {
        label: "Status: Open",
        value: "open",
        isRegexMode: false,
        onSelect: () => setActiveFilter("open"),
      },
      {
        label: "Status: Closed",
        value: "closed",
        isRegexMode: false,
        onSelect: () => setActiveFilter("closed"),
      },
    ];
    return options;
  }, []);

  if (accessDenied) {
    return <AccessDenied />;
  }

  return (
    <div className="py-8">
      <div className="absolute top-4 left-1/2 transform -translate-x-1/2 flex items-center space-x-2 z-10">
        <div className="w-[500px]">
          <SearchBox
            value={searchTerm}
            onSearch={handleSearch}
            placeholder="Filter Assignments ..."
            className="w-full"
            filterOptions={filterOptions}
            activeFilter={
              activeFilter
                ? filterOptions.find((option) => option.value === activeFilter)
                : null
            }
            onFilterClear={clearActiveFilter}
          />
        </div>

        <div className="flex mr-4">
          <button
            onClick={() => {
              if (sortField === "created_at") {
                setSortDirection(sortDirection === "asc" ? "desc" : "asc");
              } else {
                setSortField("created_at");
                setSortDirection("asc");
              }
            }}
            className="flex items-center px-3 py-2 text-sm font-medium rounded bg-gray-700 text-white hover:bg-gray-600 transition-colors"
          >
            <BiTime className="mr-2 text-blue-400" size={14} />
            <div className="flex items-center">
              <span className="mr-1">Created</span>
              <span>{getSortDirectionLabel("created_at")}</span>
            </div>
          </button>
        </div>
      </div>
      <div className="container mx-auto px-4 max-w-9xl">
        <div className="bg-gray-800 rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gray-800">
            <div className="flex justify-between items-center px-4 py-3 border-b border-gray-700">
              <h2 className="font-semibold text-lg text-white">Assignments</h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className={`p-2 rounded ${
                    page === 1
                      ? "text-gray-500 cursor-not-allowed"
                      : "text-blue-400 hover:bg-gray-700 bg-gray-800"
                  }`}
                  aria-label="Previous page"
                >
                  <FaChevronLeft size={14} />
                </button>
                <span className="flex items-center px-3 py-1 text-sm bg-gray-700 rounded">
                  Page {page}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === maxPage}
                  className={`p-2 rounded ${
                    page === maxPage
                      ? "text-gray-500 cursor-not-allowed"
                      : "text-blue-400 hover:bg-gray-700 bg-gray-800"
                  }`}
                  aria-label="Next page"
                >
                  <FaChevronRight size={14} />
                </button>
              </div>
            </div>
            <div className="h-[calc(100vh-16rem)] overflow-auto">
              <AssignmentMainContent
                assignments={filteredAssignments}
                selectedAssignmentId={selectedAssignment?.id}
                searchTerm={searchTerm}
                onSelectAssignment={handleAssignmentSelect}
                isRegexMode={isRegexMode}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modal for displaying assignment details */}
      {isAssignmentModalOpen && selectedAssignment && (
        <AssignmentModal
          assignment={selectedAssignment}
          onClose={handleAssignmentModalClose}
          onAssignmentUpdate={handleRefresh}
          userName={userName}
        />
      )}
    </div>
  );
};

export default AssignmentsDashboard;
